<script setup>
import { reactive, computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, minValue } from '@vuelidate/validators';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignsAPI from 'dashboard/api/campaigns';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import BusinessDay from 'dashboard/routes/dashboard/settings/inbox/components/BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  timeZoneOptions,
} from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';
import MessageBuilder from '../components/MessageBuilder.vue';
import AudienceBuilder from '../components/AudienceBuilder.vue';
import {
  createEmptyCampaignMessage,
  normalizeCampaignAudience,
  normalizeCampaignMessage,
  normalizeSelectedInboxIds,
  normalizeSelectedSenderValues,
  parseCampaignAudienceTargetValue,
  resolveCampaignTemplateVariables,
  selectedInboxIdsEqual,
  selectedSenderValuesEqual,
  serializeCampaignMessage,
} from '../utils/campaignForm';
import {
  normalizeCampaignStatus,
  resolveEditSubmitStatus,
} from '../utils/campaignEditStatus';

const NO_SENDER = '__none__';
const DEFAULT_CAMPAIGN_WORKING_HOURS = [
  {
    day_of_week: 0,
    closed_all_day: true,
    open_hour: null,
    open_minutes: null,
    close_hour: null,
    close_minutes: null,
    open_all_day: false,
  },
  {
    day_of_week: 1,
    closed_all_day: false,
    open_hour: 9,
    open_minutes: 0,
    close_hour: 17,
    close_minutes: 0,
    open_all_day: false,
  },
  {
    day_of_week: 2,
    closed_all_day: false,
    open_hour: 9,
    open_minutes: 0,
    close_hour: 17,
    close_minutes: 0,
    open_all_day: false,
  },
  {
    day_of_week: 3,
    closed_all_day: false,
    open_hour: 9,
    open_minutes: 0,
    close_hour: 17,
    close_minutes: 0,
    open_all_day: false,
  },
  {
    day_of_week: 4,
    closed_all_day: false,
    open_hour: 9,
    open_minutes: 0,
    close_hour: 17,
    close_minutes: 0,
    open_all_day: false,
  },
  {
    day_of_week: 5,
    closed_all_day: false,
    open_hour: 9,
    open_minutes: 0,
    close_hour: 17,
    close_minutes: 0,
    open_all_day: false,
  },
  {
    day_of_week: 6,
    closed_all_day: true,
    open_hour: null,
    open_minutes: null,
    close_hour: null,
    close_minutes: null,
    open_all_day: false,
  },
];
// Default to the browser's IANA timezone (e.g. America/Sao_Paulo) instead of UTC.
// The delivery-window comparison runs in this timezone; defaulting to UTC made a
// campaign configured in local time (e.g. Fri 09:00-21:00 BRT) evaluate against
// UTC — 3h ahead — so an evening send in Brazil already rolled into the next UTC
// day/slot and was wrongly blocked. Falls back to UTC if detection is unavailable.
const detectBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};
const DEFAULT_CAMPAIGN_TIMEZONE = detectBrowserTimezone();
const router = useRouter();
const route = useRoute();
const store = useStore();
const getters = useStoreGetters();
const { t, locale } = useI18n();
const http = window.axios;

const isEditMode = computed(
  () => route.name === 'campaigns_api_edit' && !!route.params.campaignId
);
const campaignId = computed(() => route.params.campaignId);
const accountId = computed(() => route.params.accountId);
const routeSyncKey = computed(() =>
  isEditMode.value
    ? `edit:${campaignId.value}`
    : `create:${route.name || ''}:${route.query.duplicateFrom || ''}`
);
const isLoadingCampaign = ref(false);
const showResetDialog = ref(false);
const campaignOriginalStatus = ref(null);
// Whether the loaded campaign has ever been dispatched — used to offer the
// "Apenas salvar / Zerar e redisparar" choice regardless of active/completed.
const campaignHasDispatches = ref(false);
const activeFormCampaignId = ref(null);
const resetDialogCampaignId = ref(null);
let syncedRouteKey = null;

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  contactAttributes: useMapGetter('attributes/getContactAttributes'),
  inboxes: useMapGetter('inboxes/getApiInboxes'),
  users: useMapGetter('agents/getAgents'),
  bots: useMapGetter('agentBots/getBots'),
  currentUser: useMapGetter('getCurrentUser'),
};

const createInitialAudienceState = () => ({
  mode: 'existing',
  labelIds: [],
  groupIds: [],
  contactIds: [],
  importFileName: '',
  importedHeaders: [],
  importedContacts: [],
});

const createInitialCampaignState = () => ({
  title: '',
  inboxId: null,
  inboxIds: [],
  senderSelections: [],
  enabled: true,
  parallelInboxDispatch: false,
  triggerOnlyDuringBusinessHours: false,
  campaignTimeSlots: timeSlotParse(DEFAULT_CAMPAIGN_WORKING_HOURS),
  campaignTimezone: DEFAULT_CAMPAIGN_TIMEZONE,
  messageDelayMin: 5,
  messageDelayMax: 120,
  contactDelayMin: 120,
  contactDelayMax: 600,
  messages: [createEmptyCampaignMessage(0)],
  audience: createInitialAudienceState(),
  scheduledAt: null,
  templateParams: {},
});

const state = reactive(createInitialCampaignState());

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  messageDelayMin: { required, minValue: minValue(0) },
  messageDelayMax: { required, minValue: minValue(0) },
  contactDelayMin: { required, minValue: minValue(0) },
  contactDelayMax: { required, minValue: minValue(0) },
  messages: { required, minLength: minLength(1) },
  scheduledAt: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);
const isUpdating = computed(() => formState.uiFlags.value.isUpdating);
const isSaving = computed(() => isCreating.value || isUpdating.value);
const listedCampaign = computed(() => {
  const campaigns = getters['campaigns/getApiCampaigns']?.value || [];

  return (
    campaigns.find(
      campaign => String(campaign.id) === String(campaignId.value)
    ) || null
  );
});
const isCompletedCampaign = computed(() => {
  const originalStatus = normalizeCampaignStatus(campaignOriginalStatus.value);
  const listedStatus = normalizeCampaignStatus(
    listedCampaign.value?.campaign_status
  );

  return (originalStatus || listedStatus) === 'completed';
});

const resolveEditableCampaignIdForAction = expectedCampaignId => {
  if (!isEditMode.value) {
    return null;
  }

  const routeCampaignId = String(campaignId.value || '');
  const targetCampaignId = expectedCampaignId
    ? String(expectedCampaignId)
    : routeCampaignId;

  if (
    !routeCampaignId ||
    !targetCampaignId ||
    routeCampaignId !== targetCampaignId ||
    activeFormCampaignId.value !== targetCampaignId
  ) {
    useAlert(t('CAMPAIGN.ENHANCED.EDIT.LOAD_ERROR'));
    return null;
  }

  return targetCampaignId;
};

const refreshCampaignStatus = async expectedCampaignId => {
  const editCampaignId = resolveEditableCampaignIdForAction(expectedCampaignId);
  if (!editCampaignId) {
    return null;
  }

  const response = await CampaignsAPI.show(editCampaignId);
  const freshStatus = normalizeCampaignStatus(
    response.data?.campaign_status || response.data?.status
  );

  if (freshStatus) {
    campaignOriginalStatus.value = freshStatus;
  }

  return freshStatus;
};

const currentDateTime = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
});

const resetCampaignForm = () => {
  Object.assign(state, createInitialCampaignState());
  state.scheduledAt = currentDateTime.value;
  showResetDialog.value = false;
  campaignOriginalStatus.value = null;
  activeFormCampaignId.value = null;
  resetDialogCampaignId.value = null;
};

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const selectedInboxOptions = computed(() => {
  const selectedIds = new Set((state.inboxIds || []).map(id => String(id)));
  return inboxOptions.value.filter(option =>
    selectedIds.has(String(option.value))
  );
});

const SENDER_PREFIX_USER = 'User:';
const SENDER_PREFIX_BOT = 'AgentBot:';

const parseSenderSelection = value => {
  if (!value || value === NO_SENDER) return { id: null, type: null };
  if (value.startsWith(SENDER_PREFIX_BOT)) {
    return {
      id: Number(value.slice(SENDER_PREFIX_BOT.length)),
      type: 'AgentBot',
    };
  }
  const raw = value.startsWith(SENDER_PREFIX_USER)
    ? value.slice(SENDER_PREFIX_USER.length)
    : value;
  return { id: Number(raw), type: 'User' };
};

const senderOptions = computed(() => {
  const options = [
    { value: NO_SENDER, label: t('CAMPAIGN.ENHANCED.FIELDS.SENDER.NO_SENDER') },
  ];
  const currentUser = formState.currentUser.value;

  if (currentUser?.id) {
    options.push({
      value: `${SENDER_PREFIX_USER}${currentUser.id}`,
      label: `${t('CAMPAIGN.ENHANCED.FIELDS.SENDER.MY_USER')} (${currentUser.name})`,
    });
  }

  const seen = new Set(options.map(option => option.value));
  (formState.users.value || []).forEach(user => {
    const value = `${SENDER_PREFIX_USER}${user.id}`;
    if (!seen.has(value)) {
      options.push({ value, label: user.name });
      seen.add(value);
    }
  });

  (formState.bots.value || []).forEach(bot => {
    const value = `${SENDER_PREFIX_BOT}${bot.id}`;
    options.push({
      value,
      label: `🤖 ${bot.name}`,
    });
  });

  return options;
});

const selectedSenderOptions = computed(() => {
  const selectedValues = new Set(
    normalizeSelectedSenderValues(state.senderSelections)
  );

  return senderOptions.value.filter(option => selectedValues.has(option.value));
});

const isDelayRangeValid = computed(
  () => state.messageDelayMax >= state.messageDelayMin
);

const isContactDelayRangeValid = computed(
  () => state.contactDelayMax >= state.contactDelayMin
);
const campaignTimeZoneOptions = computed(() => timeZoneOptions());
const normalizedLocale = computed(() =>
  (locale.value || 'en').replaceAll('_', '-')
);

const campaignDayNames = computed(() => {
  // Format in UTC. The reference dates below are built at UTC midnight
  // (Date.UTC), so formatting them in the browser's local timezone (e.g. BRT,
  // UTC-3) rolled every day back to the previous weekday — labelling day_of_week
  // 5 as "quinta", 6 as "sexta", etc. That mislabelling made users configure the
  // wrong weekday. Anchoring the formatter to UTC keeps label ↔ day_of_week aligned.
  const formatter = new Intl.DateTimeFormat(normalizedLocale.value, {
    weekday: 'long',
    timeZone: 'UTC',
  });

  return Array.from({ length: 7 }).reduce((result, _, dayIndex) => {
    const date = new Date(Date.UTC(2024, 0, 7 + dayIndex));
    result[dayIndex] = formatter.format(date);
    return result;
  }, {});
});
const campaignScheduleHasError = computed(() => {
  if (!state.triggerOnlyDuringBusinessHours) {
    return false;
  }

  const hasInvalidRange = state.campaignTimeSlots.some(
    slot => slot.from && !slot.valid
  );
  const hasOpenSlot = state.campaignTimeSlots.some(
    slot => slot.openAllDay || (slot.from && slot.to)
  );

  return hasInvalidRange || !hasOpenSlot;
});
const selectedInbox = computed(
  () =>
    formState.inboxes.value?.find(
      inbox => String(inbox.id) === String(state.inboxId)
    ) || null
);
// Channel::Quepasa is a dedicated channel type, not a Channel::Api - the two
// transforms in api-campaigns (getApiInboxes, apiChannelTypes) already list
// both, so the inbox reaches this page and is selectable. This check was the
// one place still matching Channel::Api alone, which switched the API campaign
// UI off for an inbox the picker had just offered.
const API_CAMPAIGN_CHANNEL_TYPES = ['Channel::Api', 'Channel::Quepasa'];

const isApiInbox = computed(() => {
  const channelType =
    selectedInbox.value?.channel_type || selectedInbox.value?.channelType;

  return API_CAMPAIGN_CHANNEL_TYPES.includes(channelType);
});
const isApiCampaignContext = computed(() => {
  const routeName = String(route.name || '');

  return routeName.startsWith('campaigns_api_') || isApiInbox.value;
});
const contactCustomAttributeVariables = computed(() =>
  (formState.contactAttributes.value || [])
    .map(attribute => String(attribute.attributeKey || '').trim())
    .filter(Boolean)
);
const importedHeaderVariables = computed(() =>
  state.audience.mode === 'import' ? state.audience.importedHeaders || [] : []
);
const campaignTemplateVariables = computed(() =>
  resolveCampaignTemplateVariables([
    ...contactCustomAttributeVariables.value,
    ...importedHeaderVariables.value,
  ])
);
const DEFAULT_VARIABLE_DESCRIPTION_KEYS = {
  name: 'NAME',
  first_name: 'FIRST_NAME',
  last_name: 'LAST_NAME',
  phone: 'PHONE',
  phone_number: 'PHONE_NUMBER',
  email: 'EMAIL',
  id: 'ID',
  identifier: 'IDENTIFIER',
  today_date: 'TODAY_DATE',
  today_datetime: 'TODAY_DATETIME',
  today_time: 'TODAY_TIME',
};
const translateVariableDescription = key => {
  const translated = t(key);
  return translated === key ? '' : translated;
};
const resolveVariableDescription = variable => {
  const defaultKey = DEFAULT_VARIABLE_DESCRIPTION_KEYS[variable];

  if (defaultKey) {
    return translateVariableDescription(
      `CAMPAIGN.ENHANCED.FIELDS.VARIABLE_SHORTCUTS.DESCRIPTIONS.${defaultKey}`
    );
  }

  if (importedHeaderVariables.value.includes(variable)) {
    return t(
      'CAMPAIGN.ENHANCED.FIELDS.VARIABLE_SHORTCUTS.DESCRIPTIONS.IMPORTED_HEADER',
      {
        variable,
      }
    );
  }

  if (contactCustomAttributeVariables.value.includes(variable)) {
    return t(
      'CAMPAIGN.ENHANCED.FIELDS.VARIABLE_SHORTCUTS.DESCRIPTIONS.CUSTOM_ATTRIBUTE',
      {
        variable,
      }
    );
  }

  return t('CAMPAIGN.ENHANCED.FIELDS.VARIABLE_SHORTCUTS.DESCRIPTIONS.GENERIC', {
    variable,
  });
};
const campaignTemplateVariableDetails = computed(() =>
  campaignTemplateVariables.value.map(variable => ({
    key: variable,
    description: resolveVariableDescription(variable),
  }))
);

const hasAudience = computed(() => {
  if (state.audience.mode === 'import') {
    return state.audience.importedContacts.length > 0;
  }

  return (
    state.audience.labelIds.length > 0 ||
    state.audience.contactIds.length > 0 ||
    state.audience.groupIds.length > 0
  );
});

const addMessage = () => {
  state.messages.push(createEmptyCampaignMessage(state.messages.length));
};

const normalizeMessageOrder = () => {
  state.messages.forEach((message, index) => {
    message.order = index;
  });
};

const removeMessage = messageId => {
  if (state.messages.length <= 1) {
    useAlert(t('CAMPAIGN.ENHANCED.ERRORS.MIN_ONE_MESSAGE'));
    return;
  }

  const index = state.messages.findIndex(message => message.id === messageId);
  if (index > -1) {
    state.messages.splice(index, 1);
    normalizeMessageOrder();
  }
};

const moveMessageUp = messageId => {
  const index = state.messages.findIndex(message => message.id === messageId);
  if (index > 0) {
    [state.messages[index - 1], state.messages[index]] = [
      state.messages[index],
      state.messages[index - 1],
    ];
    normalizeMessageOrder();
  }
};

const moveMessageDown = messageId => {
  const index = state.messages.findIndex(message => message.id === messageId);
  if (index < state.messages.length - 1) {
    [state.messages[index], state.messages[index + 1]] = [
      state.messages[index + 1],
      state.messages[index],
    ];
    normalizeMessageOrder();
  }
};

const updateMessage = (messageId, updates) => {
  const index = state.messages.findIndex(message => message.id === messageId);
  if (index > -1) {
    Object.assign(state.messages[index], updates);
  }
};

const formatToUTCString = localDateTime => {
  if (!localDateTime) return null;
  // datetime-local gives "YYYY-MM-DDTHH:mm" in local time (no timezone info).
  // new Date(string_without_tz) parses as LOCAL time in the browser,
  // so .toISOString() correctly converts local → UTC.
  return new Date(localDateTime).toISOString();
};

const prepareAudience = () => {
  if (state.audience.mode === 'import') {
    return state.audience.importedContacts;
  }

  return [
    ...state.audience.labelIds.map(id => ({ id, type: 'Label' })),
    ...state.audience.groupIds.map(id => ({ id, type: 'Group' })),
    ...state.audience.contactIds.map(value => {
      const directTarget = parseCampaignAudienceTargetValue(value);

      return {
        id: directTarget.id,
        type: directTarget.type,
      };
    }),
  ];
};

const firstMessageFallback = computed(() => {
  const firstMessage = state.messages[0];
  if (!firstMessage) return '';
  return firstMessage.content || firstMessage.fileName || '';
});

const prepareCampaignData = () => {
  const selectedSenderValues = normalizeSelectedSenderValues(
    state.senderSelections
  );
  const sender = parseSenderSelection(selectedSenderValues[0] || NO_SENDER);
  const uniqueInboxIds = normalizeSelectedInboxIds(state.inboxIds);
  const primaryInboxId = uniqueInboxIds[0] || state.inboxId;
  const templateParams = {
    ...(state.templateParams || {}),
    delivery_window: timeSlotTransform(state.campaignTimeSlots),
    delivery_window_timezone:
      state.campaignTimezone ||
      selectedInbox.value?.timezone ||
      DEFAULT_CAMPAIGN_TIMEZONE,
    selected_inbox_ids: uniqueInboxIds,
    selected_sender_values: selectedSenderValues,
    parallel_inbox_dispatch:
      uniqueInboxIds.length > 1 && Boolean(state.parallelInboxDispatch),
  };

  return {
    title: state.title,
    inbox_id: primaryInboxId,
    sender_id: sender.id,
    sender_type: sender.type,
    enabled: state.enabled,
    trigger_only_during_business_hours: state.triggerOnlyDuringBusinessHours,
    message_delay_min: state.messageDelayMin,
    message_delay_max: state.messageDelayMax,
    contact_delay_min: state.contactDelayMin,
    contact_delay_max: state.contactDelayMax,
    messages: state.messages.map(serializeCampaignMessage),
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: prepareAudience(),
    template_params: templateParams,
    message: firstMessageFallback.value,
  };
};

const saveCampaign = async (resetDispatch, expectedCampaignId = null) => {
  const editCampaignId = isEditMode.value
    ? resolveEditableCampaignIdForAction(expectedCampaignId)
    : null;

  if (isEditMode.value && !editCampaignId) {
    return;
  }

  try {
    const data = prepareCampaignData();
    if (resetDispatch) {
      data.reset_dispatch = true;
      data.campaign_status = 'active';
    }

    if (isEditMode.value && resetDispatch) {
      const response = await http.put(
        `/api/v1/accounts/${accountId.value}/campaigns/${editCampaignId}/reset_dispatch_queue`,
        {
          campaign: data,
        }
      );
      useAlert(
        t('CAMPAIGN.ENHANCED.EDIT.RESET_DIALOG.SUCCESS', {
          count: response.data?.meta?.queued_logs_count || 0,
        })
      );
      setTimeout(() => {
        router.push({
          name: 'campaigns_api_dispatch_logs',
          params: { ...route.params, campaignId: editCampaignId },
        });
      }, 300);
      return;
    }

    if (isEditMode.value) {
      await store.dispatch('campaigns/update', {
        id: editCampaignId,
        ...data,
      });
      useAlert(t('CAMPAIGN.ENHANCED.EDIT.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('campaigns/create', data);
      useAlert(t('CAMPAIGN.ENHANCED.SUCCESS_MESSAGE'));
    }
    setTimeout(() => {
      router.push({ name: 'campaigns_api_index', params: route.params });
    }, 300);
  } catch (error) {
    const msg = isEditMode.value
      ? 'CAMPAIGN.ENHANCED.EDIT.ERROR_MESSAGE'
      : 'CAMPAIGN.ENHANCED.ERROR_MESSAGE';
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    useAlert(error?.response?.message || t(msg));
  }
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();

  if (!isFormValid) {
    useAlert(t('CAMPAIGN.ENHANCED.ERRORS.VALIDATION_FAILED'));
    return;
  }

  if (!isDelayRangeValid.value) {
    useAlert(t('CAMPAIGN.ENHANCED.ERRORS.INVALID_DELAY_RANGE'));
    return;
  }

  if (!isContactDelayRangeValid.value) {
    useAlert(t('CAMPAIGN.ENHANCED.ERRORS.INVALID_CONTACT_DELAY_RANGE'));
    return;
  }

  if (campaignScheduleHasError.value) {
    useAlert(t('CAMPAIGN.ENHANCED.ERRORS.INVALID_CAMPAIGN_SCHEDULE'));
    return;
  }

  if (!hasAudience.value) {
    useAlert(t('CAMPAIGN.ENHANCED.ERRORS.NO_AUDIENCE'));
    return;
  }

  const editSubmitStatus = isEditMode.value
    ? await resolveEditSubmitStatus({
        originalStatus: campaignOriginalStatus.value,
        listedCampaign: listedCampaign.value,
        loadCampaign: () => refreshCampaignStatus(campaignId.value),
      })
    : null;

  // Show the "Apenas salvar / Zerar e redisparar" choice whenever the campaign
  // has already been dispatched (completed OR still has a live queue), so the
  // option is always available — not only for completed one-offs.
  if (
    isEditMode.value &&
    (editSubmitStatus === 'completed' || campaignHasDispatches.value)
  ) {
    resetDialogCampaignId.value = resolveEditableCampaignIdForAction();
    if (!resetDialogCampaignId.value) {
      return;
    }

    showResetDialog.value = true;
    return;
  }

  await saveCampaign(false, campaignId.value);
};

const handleResetConfirm = async () => {
  showResetDialog.value = false;
  await saveCampaign(true, resetDialogCampaignId.value);
};

const handleResetCancel = async () => {
  showResetDialog.value = false;
  await saveCampaign(false, resetDialogCampaignId.value);
};

// Dismiss the dialog without saving or resetting — pure abort.
const handleResetDismiss = () => {
  showResetDialog.value = false;
  resetDialogCampaignId.value = null;
};

const handleCancel = () => {
  router.push({ name: 'campaigns_api_index', params: route.params });
};

const updateCampaignTimeSlot = (slotIndex, slotData) => {
  state.campaignTimeSlots = state.campaignTimeSlots.map(slot =>
    slot.day === slotIndex ? slotData : slot
  );
};

const normalizeCampaignSchedule = templateParams => {
  const storedSchedule = Array.isArray(templateParams?.delivery_window)
    ? templateParams.delivery_window
    : [];

  return storedSchedule.length
    ? timeSlotParse(storedSchedule)
    : timeSlotParse(DEFAULT_CAMPAIGN_WORKING_HOURS);
};

// Fills the form state from a campaign payload. Shared by edit (loads into the
// same campaign) and duplicate (loads into a brand-new draft).
const applyCampaignToForm = campaign => {
  state.title = campaign.title || '';
  state.inboxId = campaign.inbox?.id || null;
  state.inboxIds = normalizeSelectedInboxIds(
    campaign.template_params?.selected_inbox_ids ||
      (campaign.inbox?.id ? [campaign.inbox.id] : [])
  );
  state.senderSelections = (() => {
    const selectedSenderValues = normalizeSelectedSenderValues(
      campaign.template_params?.selected_sender_values
    );

    if (selectedSenderValues.length) {
      return selectedSenderValues;
    }

    if (!campaign.sender_id) {
      return [];
    }

    const prefix =
      campaign.sender_type === 'AgentBot'
        ? SENDER_PREFIX_BOT
        : SENDER_PREFIX_USER;

    return [`${prefix}${campaign.sender_id}`];
  })();
  state.enabled = campaign.enabled ?? true;
  state.parallelInboxDispatch = Boolean(
    campaign.template_params?.parallel_inbox_dispatch
  );
  state.triggerOnlyDuringBusinessHours =
    campaign.trigger_only_during_business_hours ?? false;
  state.templateParams = campaign.template_params || {};
  state.campaignTimeSlots = normalizeCampaignSchedule(state.templateParams);
  state.campaignTimezone =
    state.templateParams?.delivery_window_timezone ||
    campaign.inbox?.timezone ||
    DEFAULT_CAMPAIGN_TIMEZONE;
  state.messageDelayMin = campaign.message_delay_min ?? 120;
  state.messageDelayMax = campaign.message_delay_max ?? 360;
  state.contactDelayMin = campaign.contact_delay_min ?? 30;
  state.contactDelayMax = campaign.contact_delay_max ?? 600;

  if (campaign.scheduled_at) {
    // scheduled_at comes as Unix timestamp (seconds). Convert to local datetime
    // string matching the datetime-local input format (YYYY-MM-DDTHH:mm).
    const utcDate = new Date(campaign.scheduled_at * 1000);
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');
    const hours = String(utcDate.getHours()).padStart(2, '0');
    const minutes = String(utcDate.getMinutes()).padStart(2, '0');
    state.scheduledAt = `${year}-${month}-${day}T${hours}:${minutes}`;
  } else {
    state.scheduledAt = null;
  }

  state.messages = campaign.messages?.length
    ? campaign.messages.map((message, index) =>
        normalizeCampaignMessage(message, index)
      )
    : [createEmptyCampaignMessage(0)];

  state.audience = campaign.audience?.length
    ? normalizeCampaignAudience(campaign.audience)
    : createInitialAudienceState();
};

// Duplicate: loads a source campaign into a NEW draft (activeFormCampaignId stays
// null, so saving creates a new campaign). Clears the schedule and re-enables so
// the copy starts clean; the user reviews and saves.
const loadCampaignForDuplicate = async () => {
  const sourceId = route.query.duplicateFrom;
  if (!sourceId) return;

  const loadRouteKey = routeSyncKey.value;
  isLoadingCampaign.value = true;
  try {
    const response = await CampaignsAPI.show(sourceId);
    if (loadRouteKey !== routeSyncKey.value) return;

    const campaign = response.data;
    if (!campaign) throw new Error('Campaign response is empty');

    resetCampaignForm();
    applyCampaignToForm(campaign);
    state.title = `${campaign.title || 'Campanha'} (cópia)`;
    state.enabled = true;
    state.scheduledAt = null;
  } catch (error) {
    if (loadRouteKey !== routeSyncKey.value) return;

    useAlert(t('CAMPAIGN.ENHANCED.EDIT.LOAD_ERROR'));
    router.push({ name: 'campaigns_api_index', params: route.params });
  } finally {
    if (loadRouteKey === routeSyncKey.value) {
      isLoadingCampaign.value = false;
    }
  }
};

const loadCampaignForEdit = async () => {
  if (!isEditMode.value) return;

  const loadRouteKey = routeSyncKey.value;
  isLoadingCampaign.value = true;
  try {
    const response = await CampaignsAPI.show(campaignId.value);
    if (loadRouteKey !== routeSyncKey.value) {
      return;
    }

    const campaign = response.data;
    if (!campaign) {
      throw new Error('Campaign response is empty');
    }

    const loadedCampaignId = campaign.id
      ? String(campaign.id)
      : String(campaignId.value || '');
    if (loadedCampaignId !== String(campaignId.value || '')) {
      throw new Error('Campaign response id does not match route');
    }

    resetCampaignForm();
    activeFormCampaignId.value = loadedCampaignId;

    campaignOriginalStatus.value = normalizeCampaignStatus(
      campaign.campaign_status || campaign.status
    );
    campaignHasDispatches.value = Boolean(
      campaign.has_dispatches || campaign.has_active_dispatches
    );

    applyCampaignToForm(campaign);
  } catch (error) {
    if (loadRouteKey !== routeSyncKey.value) {
      return;
    }

    useAlert(t('CAMPAIGN.ENHANCED.EDIT.LOAD_ERROR'));
    router.push({ name: 'campaigns_api_index', params: route.params });
  } finally {
    if (loadRouteKey === routeSyncKey.value) {
      isLoadingCampaign.value = false;
    }
  }
};

const syncCampaignFormWithRoute = async () => {
  const nextRouteKey = routeSyncKey.value;
  if (nextRouteKey === syncedRouteKey) {
    return;
  }

  syncedRouteKey = nextRouteKey;

  if (!isEditMode.value) {
    if (route.query.duplicateFrom) {
      await loadCampaignForDuplicate();
    } else {
      resetCampaignForm();
      isLoadingCampaign.value = false;
    }
    return;
  }

  await loadCampaignForEdit();
};

onMounted(async () => {
  const preloadRequests = [];

  if (!formState.labels.value?.length) {
    preloadRequests.push(store.dispatch('labels/get'));
  }
  if (!formState.contactAttributes.value?.length) {
    preloadRequests.push(store.dispatch('attributes/get'));
  }
  if (!formState.inboxes.value?.length) {
    preloadRequests.push(store.dispatch('inboxes/get'));
  }
  if (!formState.users.value?.length) {
    preloadRequests.push(store.dispatch('agents/get'));
  }
  if (!formState.bots.value?.length) {
    preloadRequests.push(store.dispatch('agentBots/get'));
  }

  if (preloadRequests.length) {
    await Promise.allSettled(preloadRequests);
  }
});

watch(
  routeSyncKey,
  () => {
    syncCampaignFormWithRoute();
  },
  { immediate: true }
);

watch(
  () => state.inboxIds,
  inboxIds => {
    const normalizedIds = normalizeSelectedInboxIds(inboxIds);

    if (!selectedInboxIdsEqual(inboxIds, normalizedIds)) {
      state.inboxIds = normalizedIds;
      return;
    }

    state.inboxId = normalizedIds[0] || null;

    if (!normalizedIds.length && (state.audience.groupIds || []).length) {
      state.audience = {
        ...state.audience,
        groupIds: [],
      };
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => state.senderSelections,
  senderSelections => {
    const normalizedValues = normalizeSelectedSenderValues(senderSelections);

    if (!selectedSenderValuesEqual(senderSelections, normalizedValues)) {
      state.senderSelections = normalizedValues;
    }
  },
  { deep: true, immediate: true }
);

watch(
  selectedInbox,
  inbox => {
    if (!inbox) {
      return;
    }

    // Inherit the inbox timezone only when it is a real, non-UTC value. A UTC
    // inbox timezone (the platform default) must NOT clobber the browser-detected
    // default, otherwise every campaign silently falls back to UTC.
    if (
      (!state.campaignTimezone ||
        state.campaignTimezone === DEFAULT_CAMPAIGN_TIMEZONE) &&
      inbox.timezone &&
      inbox.timezone !== 'UTC'
    ) {
      state.campaignTimezone = inbox.timezone;
    }
  },
  { immediate: true }
);

watch(
  listedCampaign,
  campaign => {
    if (!campaignOriginalStatus.value && campaign?.campaign_status) {
      campaignOriginalStatus.value = normalizeCampaignStatus(
        campaign.campaign_status
      );
    }
  },
  { immediate: true }
);

</script>

<template>
  <div class="flex flex-col h-full bg-n-white dark:bg-n-slate-1">
    <div
      v-if="isLoadingCampaign"
      class="flex items-center justify-center flex-1"
    >
      <div class="text-n-slate-11">
        {{ t('CAMPAIGN.ENHANCED.EDIT.LOADING') }}
      </div>
    </div>
    <template v-else>
      <div
        class="flex items-center justify-between p-6 border-b border-n-slate-6"
      >
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{
              isEditMode
                ? t('CAMPAIGN.ENHANCED.EDIT.TITLE')
                : t('CAMPAIGN.ENHANCED.CREATE.TITLE')
            }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{
              isEditMode
                ? t('CAMPAIGN.ENHANCED.EDIT.SUBTITLE')
                : t('CAMPAIGN.ENHANCED.CREATE.SUBTITLE')
            }}
          </p>
          <p
            v-if="isEditMode && isCompletedCampaign"
            class="mt-1 text-xs font-medium text-n-amber-11"
          >
            {{ t('CAMPAIGN.ENHANCED.EDIT.COMPLETED_WARNING') }}
          </p>
        </div>
        <div class="flex gap-3">
          <Button
            variant="faded"
            color="slate"
            :label="t('CAMPAIGN.ENHANCED.BUTTONS.CANCEL')"
            @click="handleCancel"
          />
          <Button
            :label="
              isEditMode
                ? t('CAMPAIGN.ENHANCED.BUTTONS.SAVE')
                : t('CAMPAIGN.ENHANCED.BUTTONS.CREATE')
            "
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="handleSubmit"
          />
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-6">
        <div class="max-w-4xl mx-auto">
          <form class="space-y-6" @submit.prevent="handleSubmit">
            <div
              class="bg-n-white dark:bg-n-slate-2 rounded-lg border border-n-slate-6 p-6"
            >
              <h2 class="text-lg font-medium text-n-slate-12 mb-4">
                {{ t('CAMPAIGN.ENHANCED.SECTIONS.BASIC_INFO') }}
              </h2>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  v-model="state.title"
                  :label="t('CAMPAIGN.ENHANCED.FIELDS.TITLE.LABEL')"
                  :placeholder="t('CAMPAIGN.ENHANCED.FIELDS.TITLE.PLACEHOLDER')"
                  :message="
                    v$.title.$error
                      ? t('CAMPAIGN.ENHANCED.FIELDS.TITLE.ERROR')
                      : ''
                  "
                  :message-type="v$.title.$error ? 'error' : 'info'"
                  class="md:col-span-2"
                />

                <div class="flex flex-col gap-1">
                  <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.ENHANCED.FIELDS.INBOX.LABEL') }}
                  </label>
                  <TagMultiSelectComboBox
                    v-model="state.inboxIds"
                    :options="inboxOptions"
                    :selected-options="selectedInboxOptions"
                    :label="t('CAMPAIGN.ENHANCED.FIELDS.INBOX.LABEL')"
                    :placeholder="
                      t('CAMPAIGN.ENHANCED.FIELDS.INBOX.PLACEHOLDER')
                    "
                    :message="t('CAMPAIGN.ENHANCED.FIELDS.INBOX.HELP')"
                    :has-error="v$.inboxId.$error"
                    :empty-state="t('CAMPAIGN.ENHANCED.FIELDS.INBOX.EMPTY')"
                    class="[&>div>button]:bg-n-alpha-black2"
                  />
                </div>

                <label
                  v-if="state.inboxIds.length > 1"
                  class="flex items-start gap-2 rounded-lg border border-n-slate-6 bg-n-alpha-black2 p-3 cursor-pointer"
                >
                  <input
                    v-model="state.parallelInboxDispatch"
                    type="checkbox"
                    class="mt-0.5 h-4 w-4 rounded border-n-slate-6"
                  />
                  <span class="text-sm text-n-slate-12">
                    <span class="font-medium">Disparo paralelo por caixa</span>
                    <span class="block text-xs text-n-slate-11">
                      Cada caixa dispara ao mesmo tempo, com sua própria fila de
                      delay entre contatos (em vez de um delay único global entre
                      todos). O delay entre mensagens do mesmo contato não muda.
                    </span>
                  </span>
                </label>

                <div class="flex flex-col gap-1">
                  <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.ENHANCED.FIELDS.SENDER.LABEL') }}
                  </label>
                  <TagMultiSelectComboBox
                    v-model="state.senderSelections"
                    :options="senderOptions"
                    :selected-options="selectedSenderOptions"
                    :placeholder="
                      t('CAMPAIGN.ENHANCED.FIELDS.SENDER.PLACEHOLDER')
                    "
                    :message="t('CAMPAIGN.ENHANCED.FIELDS.SENDER.HELP')"
                    class="[&>div>button]:bg-n-alpha-black2"
                  />
                </div>

                <Input
                  v-model="state.scheduledAt"
                  :label="t('CAMPAIGN.ENHANCED.FIELDS.SCHEDULED_AT.LABEL')"
                  type="datetime-local"
                  :min="currentDateTime"
                  :message="
                    v$.scheduledAt.$error
                      ? t('CAMPAIGN.ENHANCED.FIELDS.SCHEDULED_AT.ERROR')
                      : ''
                  "
                  :message-type="v$.scheduledAt.$error ? 'error' : 'info'"
                  class="md:col-span-2"
                />
              </div>

              <div class="mt-6 space-y-4 border-t border-n-slate-6 pt-6">
                <label
                  class="flex items-start justify-between gap-4 cursor-pointer"
                >
                  <div>
                    <h3 class="text-sm font-medium text-n-slate-12">
                      {{ t('CAMPAIGN.ENHANCED.FIELDS.ENABLED.LABEL') }}
                    </h3>
                    <p class="mt-1 text-xs text-n-slate-11">
                      {{ t('CAMPAIGN.ENHANCED.FIELDS.ENABLED.HELP') }}
                    </p>
                  </div>
                  <input
                    v-model="state.enabled"
                    type="checkbox"
                    class="mt-1 h-4 w-4 rounded border-n-slate-6 text-n-brand focus:ring-n-brand"
                  />
                </label>

                <label
                  class="flex items-start justify-between gap-4 cursor-pointer"
                >
                  <div>
                    <h3 class="text-sm font-medium text-n-slate-12">
                      {{ t('CAMPAIGN.ENHANCED.FIELDS.BUSINESS_HOURS.LABEL') }}
                    </h3>
                    <p class="mt-1 text-xs text-n-slate-11">
                      {{ t('CAMPAIGN.ENHANCED.FIELDS.BUSINESS_HOURS.HELP') }}
                    </p>
                  </div>
                  <input
                    v-model="state.triggerOnlyDuringBusinessHours"
                    type="checkbox"
                    class="mt-1 h-4 w-4 rounded border-n-slate-6 text-n-brand focus:ring-n-brand"
                  />
                </label>

                <div
                  v-if="state.triggerOnlyDuringBusinessHours"
                  class="rounded-lg border border-n-slate-6 p-4 space-y-4"
                >
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="flex flex-col gap-1">
                      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                        {{
                          t('CAMPAIGN.ENHANCED.FIELDS.CAMPAIGN_TIMEZONE.LABEL')
                        }}
                      </label>
                      <ComboBox
                        v-model="state.campaignTimezone"
                        :options="campaignTimeZoneOptions"
                        :placeholder="
                          t(
                            'CAMPAIGN.ENHANCED.FIELDS.CAMPAIGN_TIMEZONE.PLACEHOLDER'
                          )
                        "
                      />
                    </div>
                  </div>

                  <div class="space-y-1">
                    <p class="text-sm font-medium text-n-slate-12">
                      {{
                        t(
                          'CAMPAIGN.ENHANCED.FIELDS.BUSINESS_HOURS.WEEKLY_LABEL'
                        )
                      }}
                    </p>
                    <p class="text-xs text-n-slate-11">
                      {{
                        t('CAMPAIGN.ENHANCED.FIELDS.BUSINESS_HOURS.WEEKLY_HELP')
                      }}
                    </p>
                  </div>

                  <div class="rounded-lg border border-n-slate-6 px-4">
                    <BusinessDay
                      v-for="timeSlot in state.campaignTimeSlots"
                      :key="timeSlot.day"
                      :day-name="campaignDayNames[timeSlot.day]"
                      :time-slot="timeSlot"
                      @update="
                        data => updateCampaignTimeSlot(timeSlot.day, data)
                      "
                    />
                  </div>

                  <p
                    v-if="campaignScheduleHasError"
                    class="text-sm text-red-600"
                  >
                    {{
                      t('CAMPAIGN.ENHANCED.ERRORS.INVALID_CAMPAIGN_SCHEDULE')
                    }}
                  </p>
                </div>
              </div>
            </div>

            <div
              class="bg-n-white dark:bg-n-slate-2 rounded-lg border border-n-slate-6 p-6"
            >
              <h2 class="text-lg font-medium text-n-slate-12 mb-4">
                {{ t('CAMPAIGN.ENHANCED.SECTIONS.MESSAGE_DELAY') }}
              </h2>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  v-model.number="state.messageDelayMin"
                  type="number"
                  :label="t('CAMPAIGN.ENHANCED.FIELDS.DELAY_MIN.LABEL')"
                  :placeholder="
                    t('CAMPAIGN.ENHANCED.FIELDS.DELAY_MIN.PLACEHOLDER')
                  "
                  min="0"
                />

                <Input
                  v-model.number="state.messageDelayMax"
                  type="number"
                  :label="t('CAMPAIGN.ENHANCED.FIELDS.DELAY_MAX.LABEL')"
                  :placeholder="
                    t('CAMPAIGN.ENHANCED.FIELDS.DELAY_MAX.PLACEHOLDER')
                  "
                  min="0"
                />
              </div>

              <p v-if="!isDelayRangeValid" class="mt-2 text-sm text-red-600">
                {{ t('CAMPAIGN.ENHANCED.ERRORS.INVALID_DELAY_RANGE') }}
              </p>
              <p v-else class="mt-2 text-sm text-n-slate-11">
                {{
                  t('CAMPAIGN.ENHANCED.FIELDS.DELAY_HELP', {
                    min: state.messageDelayMin,
                    max: state.messageDelayMax,
                  })
                }}
              </p>
            </div>

            <div
              class="bg-n-white dark:bg-n-slate-2 rounded-lg border border-n-slate-6 p-6"
            >
              <h2 class="text-lg font-medium text-n-slate-12 mb-4">
                {{ t('CAMPAIGN.ENHANCED.SECTIONS.CONTACT_DELAY') }}
              </h2>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  v-model.number="state.contactDelayMin"
                  type="number"
                  :label="t('CAMPAIGN.ENHANCED.FIELDS.CONTACT_DELAY_MIN.LABEL')"
                  :placeholder="
                    t('CAMPAIGN.ENHANCED.FIELDS.CONTACT_DELAY_MIN.PLACEHOLDER')
                  "
                  min="0"
                />

                <Input
                  v-model.number="state.contactDelayMax"
                  type="number"
                  :label="t('CAMPAIGN.ENHANCED.FIELDS.CONTACT_DELAY_MAX.LABEL')"
                  :placeholder="
                    t('CAMPAIGN.ENHANCED.FIELDS.CONTACT_DELAY_MAX.PLACEHOLDER')
                  "
                  min="0"
                />
              </div>

              <p
                v-if="!isContactDelayRangeValid"
                class="mt-2 text-sm text-red-600"
              >
                {{ t('CAMPAIGN.ENHANCED.ERRORS.INVALID_CONTACT_DELAY_RANGE') }}
              </p>
              <p v-else class="mt-2 text-sm text-n-slate-11">
                {{
                  t('CAMPAIGN.ENHANCED.FIELDS.CONTACT_DELAY_HELP', {
                    min: state.contactDelayMin,
                    max: state.contactDelayMax,
                  })
                }}
              </p>
            </div>

            <div
              class="bg-n-white dark:bg-n-slate-2 rounded-lg border border-n-slate-6 p-6"
            >
              <h2 class="text-lg font-medium text-n-slate-12 mb-4">
                {{ t('CAMPAIGN.ENHANCED.SECTIONS.AUDIENCE') }}
              </h2>

              <AudienceBuilder
                v-model="state.audience"
                :account-id="route.params.accountId"
                :inbox-id="state.inboxId"
                :is-api-inbox="isApiCampaignContext"
                :labels="formState.labels.value"
                :available-variables="campaignTemplateVariables"
                :available-variable-details="campaignTemplateVariableDetails"
                @error="useAlert($event)"
              />

              <p class="mt-1 text-xs text-n-slate-11">
                {{ t('CAMPAIGN.ENHANCED.FIELDS.AUDIENCE.HELP') }}
              </p>
            </div>

            <div
              class="bg-n-white dark:bg-n-slate-2 rounded-lg border border-n-slate-6 p-6"
            >
              <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.ENHANCED.SECTIONS.MESSAGES') }}
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  :label="t('CAMPAIGN.ENHANCED.BUTTONS.ADD_MESSAGE')"
                  @click="addMessage"
                />
              </div>

              <div class="space-y-4">
                <MessageBuilder
                  v-for="(message, index) in state.messages"
                  :key="message.id"
                  :message="message"
                  :index="index"
                  :total="state.messages.length"
                  :account-id="route.params.accountId"
                  :available-variables="campaignTemplateVariables"
                  :available-variable-details="campaignTemplateVariableDetails"
                  @update="updateMessage(message.id, $event)"
                  @remove="removeMessage(message.id)"
                  @move-up="moveMessageUp(message.id)"
                  @move-down="moveMessageDown(message.id)"
                  @error="useAlert($event)"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </template>

    <!-- Reset Dispatch Dialog -->
    <teleport to="body">
      <div
        v-if="showResetDialog"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="handleResetDismiss"
        @keydown.esc="handleResetDismiss"
      >
        <div
          class="bg-n-white dark:bg-n-slate-2 rounded-xl shadow-xl p-6 max-w-md w-full mx-4"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('CAMPAIGN.ENHANCED.EDIT.RESET_DIALOG.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-1">
            {{ t('CAMPAIGN.ENHANCED.EDIT.RESET_DIALOG.MESSAGE') }}
          </p>
          <p class="text-xs text-n-amber-11 mb-4">
            {{ t('CAMPAIGN.ENHANCED.EDIT.RESET_DIALOG.WARNING') }}
          </p>
          <div class="flex justify-end items-center gap-3">
            <Button
              variant="ghost"
              color="slate"
              class="mr-auto"
              :label="t('CAMPAIGN.ENHANCED.EDIT.RESET_DIALOG.CANCEL')"
              @click="handleResetDismiss"
            />
            <Button
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.ENHANCED.EDIT.RESET_DIALOG.KEEP')"
              @click="handleResetCancel"
            />
            <Button
              color="ruby"
              :label="t('CAMPAIGN.ENHANCED.EDIT.RESET_DIALOG.RESET')"
              @click="handleResetConfirm"
            />
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
